import React from 'react'
import { Flex, Text, BaseButton } from '../../vanilla'
import { ExclamationCircleIcon, RapidDriverIcon, RapidThumbIcon, LocationIcon } from '../icons'
import { useSlotExpiration } from '../../store/hooks/useSlotExpiration'
import { RapidDeliveryETA } from './rapid-delivery-eta'
import { Link } from 'react-router-dom'
import { useRapidDeliveryStore, useBasketStore } from '../../store/hooks/useStore'

interface RapidDeliveryDateTimeProps {
  popover?: boolean
  isBasket?: boolean
  isMiniBasket?: boolean
  isCheckout?: boolean
  isHomepage?: boolean
}

export const RapidDeliveryDateTime = ({
  isBasket = false,
  popover = false,
  isMiniBasket = false,
  isCheckout = false,
  isHomepage = false
} : RapidDeliveryDateTimeProps) => {
  const { isReservationExpired } = useSlotExpiration()
  const {isRapidDeliveryBooked} = useRapidDeliveryStore()
  const {addressDisplay} = useBasketStore()
  const isExpired = isReservationExpired()
  const activeRapidDeliverySlot = !isExpired && isRapidDeliveryBooked

  const renderRapidDeliverySVG = () => {
    if (isMiniBasket && !activeRapidDeliverySlot) return null
    if (isBasket) return <RapidDriverIcon color="accent0" boxSize="60px" />
    return <RapidThumbIcon boxSize={'60px'} />
  }

  const expiryMessage = () => (
    <Flex gap="4px" alignItems="center">
      {!isBasket && <ExclamationCircleIcon color="accent0" marginBottom="2px" flexShrink="0" />}
      <Text color="accent0" variant="text4" data-test-selector="rapid-delivery-reservation-expired">
        Your Rapid Delivery reservation has expired!
      </Text>
    </Flex>
  )

  const defaultHeader = !isBasket && !isCheckout ? (
    isExpired ? expiryMessage() : (
      <Text color="accent0" variant="text4">
        Your Rapid Delivery
      </Text>
    )
  ) : null

  const defaultBody = isBasket || isCheckout ? (
    <Flex flexDirection="column" gap="4px">
      {isExpired ? expiryMessage() : (
        <Text color="accent0" variant="text4">
          Your Rapid Delivery:
        </Text>
      )}
      <RapidDeliveryETA isBasket={isBasket} isCheckout={isCheckout} />
    </Flex>
  ) : (
    <Flex gap="8px" width="full" justify="space-between">
      <RapidDeliveryETA isBasket={isBasket} isCheckout={isCheckout} />
    </Flex>
  )

  if (isHomepage) {
    return (
      <Flex
        gap="16px"
        alignItems="center"
        data-test-selector="rapid-delivery-date-time"
        flexDirection="row"
      >
        {renderRapidDeliverySVG()}
        <Flex flexDirection="column" gap="4px">
          {isExpired && expiryMessage()}
          <RapidDeliveryETA isHomepage />
          {!isExpired && (
            <Flex gap="2px" align="flex-start" marginTop="2px">
              <LocationIcon
                width="16px"
                height="auto"
                color="accent1"
                flexShrink="0"
                marginTop="4px"
              />
              <Text
                variant="text3"
                color="accent1"
                textDecoration="underline"
                data-test-selector="delivery-address-link"
              >
                {addressDisplay}
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
    )
  }

  if (isMiniBasket || popover) {
    return (
      <Flex
        gap="8px"
        align="flex-start"
        data-test-selector="rapid-delivery-date-time"
        flexDirection="row"
      >
        {renderRapidDeliverySVG()}
        <Flex flexDirection="column" width="full" gap="4px">
          <Text color="accent0" variant={!popover ? 'text3' : 'text4'}>
            {isExpired ? expiryMessage() : 'Your Rapid Delivery'}
          </Text>
          <Flex gap="4px" justifyContent="space-between" width="full">
            <RapidDeliveryETA isMiniBasket={isMiniBasket} popover={popover} />
            {isMiniBasket && (
              <BaseButton
                variant="outlinePrimary"
                size="sm"
                data-test-selector="rapid-delivery-change-link"
                alignSelf="flex-end"
                flexBasis={['half', 'auto']}
                as={Link}
                to='/select-delivery'
                paddingX="8px"
                paddingY="6px"
                minHeight="36px"
                height="auto"
              >
                <Text variant="unstyled" fontSize="xs" lineHeight="inherit" fontWeight="inherit" align="center">
                  {isExpired ? 'Book Now' : 'Change Delivery'}
                </Text>
              </BaseButton>
            )}
          </Flex>
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex
      gap="16px"
      align={popover || isBasket ? 'flex-start' : 'center'}
      data-test-selector="rapid-delivery-date-time"
      flexDirection="row"
      paddingX={isCheckout ? '16px' : '0px'}
    >
      <Flex gap="16px" alignItems="center">
        {renderRapidDeliverySVG()}
        {defaultHeader}
      </Flex>

      {defaultBody}
    </Flex>
  )
}